/**
 * Generates the API content for a static error page based on the provided parameters.
 *
 * @param {Object} props
 * @param {import("react-intl").IntlShape} props.intl - The react-intl object used for formatting localized messages.
 * @param {string} props.pathname - The current page path.
 * @param {number} props.statusCode - The HTTP status code for the error.
 * @param {import("~/config/useGetRichtTextValues").RichTextValues} props.richTextValues
 * @returns {LandingPageApiProps} - The structured API content for the error page.
 */
export const getErrorPageApiContent = ({
  intl,
  pathname,
  statusCode,
  richTextValues,
}) => {
  const isJobDetailPage =
    typeof pathname === 'string' && pathname.indexOf('job-detail') !== -1;
  return {
    type: 'LANDING_PAGE',
    id: 'static-error-page',
    path: pathname,
    language: intl.locale,
    withShare: false,
    meta: {
      title: intl.formatMessage({
        id: 'errorpage.pageTitle',
      }),
      description: '',
    },
    content: [
      {
        type: 'CONTAINER_LARGE_B',
        style: 'NONE',
        format: 'NARROW_WIDE',
        columns: [
          [
            {
              type: 'HEADING',
              level: 1,
              text: intl.formatMessage(
                isJobDetailPage
                  ? {
                      id: 'errorpageJobDetail.bodyHeadline',
                    }
                  : {
                      id: 'errorPage.bodyHeadline',
                    },
                {
                  status: statusCode,
                },
              ),
            },
            {
              type: 'TEXT',
              html: `${intl.formatMessage(
                isJobDetailPage
                  ? {
                      id: 'errorpageJobDetail.bodySubheadline',
                    }
                  : {
                      id: 'errorPage.bodySubheadline',
                    },
              )}${intl.formatMessage(
                { id: 'errorpage.bodyMainContent' },
                {
                  homeUrl: (chunks) => (
                    <a href={richTextValues.homeUrl}>{chunks}</a>
                  ),
                  jobSearchUrl: (chunks) => (
                    <a href={richTextValues.jobSearchUrl}>{chunks}</a>
                  ),
                  healthgroupUrl: (chunks) => (
                    <a href={richTextValues.jobSearchUrl}>{chunks}</a>
                  ),
                  searchUrl: (chunks) => (
                    <a href={richTextValues.jobSearchUrl}>{chunks}</a>
                  ),
                },
              )}${intl.formatMessage(
                { id: 'errorPage.bodyContactContent' },
                {
                  contactEmail: (chunks) => (
                    <a href={`mailto:${richTextValues.contactEmail}`}>
                      {chunks}
                    </a>
                  ),
                },
              )}`,
            },
            {
              type: 'CTA',
              alignment: 'LEFT',
              items: [
                {
                  type: 'PRIMARY',
                  link: {
                    url: intl.formatMessage(
                      isJobDetailPage
                        ? {
                            id: 'errorpageJobDetail.bodyCtaUrl',
                          }
                        : {
                            id: 'errorpage.bodyCtaUrl',
                          },
                    ),
                    text: intl.formatMessage(
                      isJobDetailPage
                        ? { id: 'errorpageJobDetail.bodyCtaText' }
                        : { id: 'errorpage.bodyCtaText' },
                    ),
                    title: intl.formatMessage(
                      isJobDetailPage
                        ? {
                            id: 'errorpageJobDetail.bodyCtaTitle',
                          }
                        : {
                            id: 'errorpage.bodyCtaTitle',
                          },
                    ),
                    target: 'SELF',
                  },
                  icon: 'ARROW_ANIMATION',
                },
              ],
            },
          ],
          [
            {
              type: 'IMAGE',
              image: {
                alt: intl.formatMessage({
                  id: 'errorpage.bodyCtaUrl',
                }),
                url: '/assets/images/error.svg',
                title: '',
              },
            },
          ],
        ],
      },
    ],
  };
};
